import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

export interface Collection {
  id: string
  name: string
  slug: string
  description?: string
  collection_type: 'manual' | 'automatic'
  rules: Record<string, any>
  sort_order: number
  is_active: boolean
  is_featured: boolean
  image?: string
  banner_image?: string
  meta_title?: string
  meta_description?: string
  meta_keywords?: string
  collection_metadata: Record<string, any>
  created_at: string
  updated_at: string
}

export interface CollectionCreate {
  name: string
  slug?: string
  description?: string
  collection_type?: 'manual' | 'automatic'
  rules?: Record<string, any>
  sort_order?: number
  is_active?: boolean
  is_featured?: boolean
  image?: string
  banner_image?: string
  meta_title?: string
  meta_description?: string
  meta_keywords?: string
  collection_metadata?: Record<string, any>
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  per_page: number
  pages: number
}

export const collectionApi = createApi({
  reducerPath: 'collectionApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Collection'],
  endpoints: (builder) => ({
    getCollections: builder.query<PaginatedResponse<Collection>, {
      page?: number
      per_page?: number
      search?: string
      collection_type?: string
      is_active?: boolean
      is_featured?: boolean
    }>({
      query: (params) => ({
        url: '/collections',
        params,
      }),
      providesTags: ['Collection'],
    }),
    getCollection: builder.query<Collection, string>({
      query: (id) => `/collections/${id}`,
      providesTags: ['Collection'],
    }),
    createCollection: builder.mutation<Collection, CollectionCreate>({
      query: (data) => ({
        url: '/collections',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Collection'],
    }),
    updateCollection: builder.mutation<Collection, { id: string; data: Partial<CollectionCreate> }>({
      query: ({ id, data }) => ({
        url: `/collections/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Collection'],
    }),
    deleteCollection: builder.mutation<void, string>({
      query: (id) => ({
        url: `/collections/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Collection'],
    }),
  }),
})

export const {
  useGetCollectionsQuery,
  useGetCollectionQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useDeleteCollectionMutation,
} = collectionApi